import type { ColumnConfiguration, FieldConfiguration } from '../../types';

/**
 * PD-17093 — Jobs list columns, shared by the Jobs page and a customer's Jobs
 * tab. Rows are `job` records from GET /api/jobs (JobListRowDto): the stored
 * quoted, ordered and invoiced totals, plus the customer, salesperson and
 * opportunity names the API joins.
 */
export const JOB_TABLE = 'job';

export const JOB_STATUS_VALUE_MAPPING = [
	{ value: 'planned', label: 'Planned', color: { type: 'info' } },
	{ value: 'active', label: 'Active', color: { type: 'success' } },
	{ value: 'on_hold', label: 'On Hold', color: { type: 'warning' } },
	{ value: 'closed', label: 'Closed', color: { type: 'neutral' } },
];

const lineBreak = (
	id: string,
	visibleWhen?: FieldConfiguration['conditionalVisibility'],
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
	...(visibleWhen != null ? { conditionalVisibility: visibleWhen } : {}),
});

const secondaryText = (id: string, fieldName: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable: JOB_TABLE } },
	styleConfig: {
		size: 'sm',
		colorRule: { type: 'theme', themeColor: 'textSecondary' },
	},
	displayStyle: 'text',
	conditionalVisibility: { field: fieldName, condition: 'is_not_null' },
});

const money = (
	id: string,
	fieldName: string,
	header: string,
	options: { defaultValue?: string; secondary?: FieldConfiguration } = {},
): ColumnConfiguration => ({
	id,
	fields: [
		{
			id: `${id}-field`,
			dataConfig: {
				type: 'field',
				field: { fieldName, objectTable: JOB_TABLE },
				defaultValue: options.defaultValue ?? '-',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
				formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
			},
			displayStyle: 'currency',
		},
		...(options.secondary != null
			? [lineBreak(`${id}-lb`, options.secondary.conditionalVisibility), options.secondary]
			: []),
	],
	headers: [{ id: `h-${id}`, text: header }],
	minWidth: 120,
	sortable: true,
	alignment: 'right',
	sortFields: [{ direction: 'DESC', fieldName, objectTable: JOB_TABLE }],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

export const jobNameColumn = (): ColumnConfiguration => ({
	id: 'job-name',
	fields: [
		{
			id: 'job-name-field',
			link: { type: 'object', enabled: true, objectTable: JOB_TABLE, idField: 'id' },
			dataConfig: { type: 'field', field: { fieldName: 'name', objectTable: JOB_TABLE } },
			styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		// PD-17450 — a sub-job's path under its name, so two jobs called
		// "Mechanical" cannot be confused (search results, the Flat list).
		lineBreak('job-name-lb-path', { field: 'pathLabel', condition: 'is_not_null' }),
		secondaryText('job-path', 'pathLabel'),
		lineBreak('job-name-lb1', { field: 'jobNumberLabel', condition: 'is_not_null' }),
		secondaryText('job-number', 'jobNumberLabel'),
	],
	headers: [{ id: 'h-job', text: 'Job' }],
	minWidth: 200,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'name', objectTable: JOB_TABLE }],
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

export const jobCustomerColumn = (): ColumnConfiguration => ({
	id: 'job-customer',
	fields: [
		{
			id: 'job-customer-field',
			link: { type: 'object', enabled: true, objectTable: 'client', idField: 'fkClientId' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: JOB_TABLE },
				defaultValue: '-',
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		// PD-17448 — a job can have several customers; the main one is named.
		lineBreak('job-customer-lb', { field: 'otherCustomersLabel', condition: 'is_not_null' }),
		secondaryText('job-customer-others', 'otherCustomersLabel'),
	],
	headers: [{ id: 'h-customer', text: 'Customer' }],
	minWidth: 180,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * PD-17448 — on a customer's Jobs tab: that customer's role and status on
 * each job ("General contractor · Awarded").
 */
export const jobCustomerRoleColumn = (): ColumnConfiguration => ({
	id: 'job-customer-role',
	fields: [
		{
			id: 'job-customer-role-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'customerRoleLabel', objectTable: JOB_TABLE },
				defaultValue: '-',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-customer-role', text: 'Role · Status' }],
	minWidth: 160,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

export const jobStatusColumn = (): ColumnConfiguration => ({
	id: 'job-status',
	fields: [
		{
			id: 'job-status-field',
			dataConfig: { type: 'field', field: { fieldName: 'status', objectTable: JOB_TABLE } },
			styleConfig: { size: 'sm', valueMapping: JOB_STATUS_VALUE_MAPPING },
			displayStyle: 'badge',
		},
	],
	headers: [{ id: 'h-status', text: 'Status' }],
	minWidth: 100,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'ASC', fieldName: 'status', objectTable: JOB_TABLE }],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

export const jobSalespersonColumn = (): ColumnConfiguration => ({
	id: 'job-salesperson',
	fields: [
		{
			id: 'job-salesperson-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'salespersonName', objectTable: JOB_TABLE },
				defaultValue: 'Unassigned',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-salesperson', text: 'Salesperson' }],
	minWidth: 130,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

export const jobDatesColumn = (): ColumnConfiguration => ({
	id: 'job-dates',
	fields: [
		{
			id: 'job-dates-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'datesLabel', objectTable: JOB_TABLE },
				defaultValue: '-',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-dates', text: 'Start – End' }],
	minWidth: 150,
	sortable: true,
	alignment: 'left',
	sortFields: [{ direction: 'DESC', fieldName: 'startDate', objectTable: JOB_TABLE }],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

export const jobEstimateColumn = (): ColumnConfiguration =>
	money('job-estimate', 'estimatedValue', 'Estimated Value', {
		defaultValue: 'Not estimated',
	});

export const jobOrderedColumn = (): ColumnConfiguration =>
	money('job-ordered', 'orderedTotal', 'Ordered', {
		defaultValue: '$0',
		secondary: secondaryText('job-ordered-share', 'percentOfEstimateLabel'),
	});

export const jobQuotedColumn = (): ColumnConfiguration =>
	money('job-quoted', 'quotedTotal', 'Quoted', { defaultValue: '$0' });

export const jobInvoicedColumn = (): ColumnConfiguration =>
	money('job-invoiced', 'invoicedTotal', 'Invoiced', { defaultValue: '$0' });

/** The Jobs page's default columns. */
export function createDefaultJobColumns(): Array<ColumnConfiguration> {
	return [
		jobNameColumn(),
		jobCustomerColumn(),
		jobStatusColumn(),
		jobSalespersonColumn(),
		jobDatesColumn(),
		jobEstimateColumn(),
		jobOrderedColumn(),
		jobInvoicedColumn(),
	];
}

/**
 * A customer's Jobs tab: the same, without the customer column, and with the
 * customer's role and status on each job (PD-17448: the tab lists every job
 * the customer is on).
 */
export function createClientJobColumns(): Array<ColumnConfiguration> {
	return [
		jobNameColumn(),
		jobCustomerRoleColumn(),
		jobStatusColumn(),
		jobSalespersonColumn(),
		jobDatesColumn(),
		jobEstimateColumn(),
		jobQuotedColumn(),
		jobOrderedColumn(),
		jobInvoicedColumn(),
	];
}
